import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { catalogApi } from '@/lib/api';
import { Swatch } from '@/components/Swatch';
import { Seo } from '@/components/Seo';
import { useLocale, useLocalePath } from '@/hooks/useLocalePath';
import { SofaSilhouette } from '@/components/SofaSilhouette';
import type { JSX } from 'react';

/**
 * The Material Wall — the signature element. 183 fabrics fold to ~26 families; selecting a
 * swatch previews that colour on one shape, so two fabrics can be compared without being
 * compared through two product photographs shot in two different lights.
 */
export function FabricsPage(): JSX.Element {
  const { t } = useTranslation();
  const locale = useLocale();
  const path = useLocalePath();
  const [params, setParams] = useSearchParams();
  const activeFamily = params.get('rodzina');
  /**
   * Which colour is being previewed, and which family row it belongs to.
   *
   * The row matters: below `lg` the preview panel cannot sit in a sticky column, so it is
   * rendered inline under the swatches that were tapped. Without the family slug there is
   * no way to know which row that is.
   */
  const [preview, setPreview] = useState<{ hex: string[]; label: string; family: string } | null>(
    null,
  );

  const { data: families, isLoading } = useQuery({
    queryKey: ['fabrics', locale],
    queryFn: () => catalogApi.fabrics(locale),
    staleTime: 5 * 60_000,
  });

  /*
   * Selection and preview are different things, and conflating them broke both.
   *
   * `selected` fell back to the first family, so with nothing chosen the first row rendered
   * with the selected ring — and clicking it then appeared to do nothing, because it
   * already looked chosen. Nothing is selected until something is selected.
   */
  const selected = (families ?? []).find((f) => f.slug === activeFamily) ?? null;
  /** What the preview panel shows: the chosen family, else the first, so it is never empty. */
  const previewed = selected ?? families?.[0] ?? null;

  return (
    <>
      <Seo title={t('fabrics.title')} description={t('fabrics.lead')} path="/tkaniny" />

      <div className="rz-shell py-10">
        <p className="rz-eyebrow mb-3">{t('nav.fabrics')}</p>
        <h1 className="text-xl md:text-2xl max-w-2xl">{t('fabrics.title')}</h1>
        <p className="mt-4 max-w-xl text-sm text-[var(--color-ink-soft)] leading-relaxed">{t('fabrics.lead')}</p>
      </div>

      <div className="rz-shell grid gap-10 pb-24 lg:grid-cols-[1fr_380px]">
        <div className="space-y-2">
          {isLoading
            ? Array.from({ length: 8 }, (_, i) => <div key={i} className="rz-skeleton h-20" />)
            : (families ?? []).map((family, index) => (
                <motion.div
                  key={family.slug}
                  initial={{ opacity: 0, x: -8 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.35, delay: Math.min(index, 10) * 0.03 }}
                  className="p-4 transition-colors duration-[var(--dur-hover)] ease-[var(--ease-spring)]"
                  style={{
                    background: family.slug === activeFamily ? 'var(--color-ground-lift)' : 'transparent',
                    boxShadow: family.slug === activeFamily ? 'inset 0 0 0 1px var(--color-brand)' : undefined,
                  }}
                >
                  {/*
                    The family toggle and the swatches are siblings, not nested. They used
                    to be one button wrapping the other, which is invalid, unreachable by
                    keyboard as two controls, and meant tapping a colour toggled the family
                    instead of previewing the colour.
                  */}
                  <div className="flex flex-wrap items-center gap-x-5 gap-y-3">
                    {/* Fills the row up to the swatches. It used to be 144px wide, so most
                        of the row was dead space that looked clickable and was not. */}
                    <button
                      type="button"
                      onClick={() => setParams(family.slug === activeFamily ? {} : { rodzina: family.slug })}
                      aria-pressed={family.slug === activeFamily}
                      className="min-w-36 flex-1 self-stretch text-left"
                    >
                      <p className="font-semibold text-sm capitalize">{family.label}</p>
                      <p className="text-2xs text-[var(--color-muted)]">
                        {t('fabrics.usedIn', { count: family.productCount })}
                      </p>
                    </button>
                    <div className="flex flex-wrap gap-1.5">
                      {family.colors.map((color) => {
                        const active =
                          preview?.family === family.slug && preview.label === color.label;
                        return (
                          <button
                            key={color.slug}
                            type="button"
                            aria-pressed={active}
                            aria-label={color.label}
                            /* Tap is the point: hover alone never fires on a touch screen,
                               so on a phone pressing a colour did nothing at all. */
                            onClick={() => setPreview({ hex: color.hex, label: color.label, family: family.slug })}
                            onMouseEnter={() => setPreview({ hex: color.hex, label: color.label, family: family.slug })}
                            onFocus={() => setPreview({ hex: color.hex, label: color.label, family: family.slug })}
                            className="rounded-full transition-transform duration-[var(--dur-tap)] ease-[var(--ease-spring)] hover:scale-110 active:scale-95 motion-reduce:transition-none motion-reduce:hover:scale-100"
                            style={{ outline: active ? '2px solid var(--color-brand)' : undefined, outlineOffset: 2 }}
                          >
                            <Swatch hex={color.hex} label={color.label} size={22} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Below `lg` the sticky column does not exist, so the preview appears
                      here, under the colours that were pressed, rather than at the foot of
                      a long list where it is off screen and looks like nothing happened. */}
                  {preview?.family === family.slug && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                      className="overflow-hidden lg:hidden"
                      data-testid="inline-fabric-preview"
                    >
                      <div className="mt-4 flex items-center gap-4 bg-[var(--color-ground-deep)] p-4">
                        <div className="w-28 shrink-0">
                          <SofaSilhouette hex={preview.hex} />
                        </div>
                        <div>
                          <p className="text-sm font-semibold">{preview.label}</p>
                          <p className="text-xs text-[var(--color-muted)]">
                            {family.label} · {t('fabrics.colorsInFamily', { count: family.colors.length })}
                          </p>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              ))}
        </div>

        <aside className="hidden space-y-4 lg:block lg:sticky lg:top-24 lg:self-start">
          <p className="rz-eyebrow">{t('fabrics.previewOn')}</p>
          <div className="bg-[var(--color-ground-deep)] p-6">
            <SofaSilhouette hex={preview?.hex ?? previewed?.colors[0]?.hex ?? ['#8A8F96']} />
          </div>
          <p className="text-sm font-semibold">{preview?.label ?? previewed?.colors[0]?.label ?? ''}</p>
          {previewed && (
            <p className="text-xs text-[var(--color-muted)]">
              {previewed.label} · {t('fabrics.colorsInFamily', { count: previewed.colors.length })}
            </p>
          )}
          {/* The library was a dead end: it showed the materials and offered no way to see
              anything made of them. */}
          {previewed && (
            <Link
              to={`${path('/kategoria/narozniki')}?tkanina=${previewed.slug}`}
              className="rz-button rz-button--ghost w-full"
            >
              {t('fabrics.showProducts')}
            </Link>
          )}
        </aside>
      </div>
    </>
  );
}
